import { BoundingBox, Camera, Color3, Frustum, Matrix, Mesh, StandardMaterial, Vector3, VertexData, } from '@babylonjs/core';
import { DEFAULT_POINT_BUDGET, MAX_LOADS_TO_GPU, MAX_NUM_NODES_LOADING, } from './constants';
import { OctreeGeometryNode } from './loading-v2/OctreeGeometryNode';
import { loadOctree } from './loading-v2/load-octree';
import { PointCloudOctree } from './point-cloud-octree';
import { isTreeNode, isGeometryNode } from './type-predicates';
import { BinaryHeap } from './utils/binary-heap';
import { Box3Helper } from './utils/box3-helper';
import { LRU } from './utils/lru';
export class QueueItem {
    pointCloudIndex;
    weight;
    node;
    parent;
    constructor(pointCloudIndex, weight, node, parent) {
        this.pointCloudIndex = pointCloudIndex;
        this.weight = weight;
        this.node = node;
        this.parent = parent;
    }
}
export class Potree {
    _pointBudget = DEFAULT_POINT_BUDGET;
    maxNumNodesLoading = MAX_NUM_NODES_LOADING;
    frustumMeshes = [];
    lru = new LRU(this._pointBudget);
    async loadPointCloud(name, metadataUrl, getUrl, xhrRequest = (input, init) => {
        return fetch(input, init);
    }) {
        const fileName = metadataUrl
            .split(/\/|%2F/)
            .pop()
            ?.split('?')?.[0] || '';
        if (fileName === 'metadata.json') {
            return await loadOctree(metadataUrl, getUrl, xhrRequest).then((geometry) => {
                return new PointCloudOctree(name, this, geometry);
            });
        }
        throw new Error('Unsupported file type');
    }
    updatePointClouds(pointClouds, camera, engine) {
        const result = this.updateVisibility(pointClouds, camera, engine);
        for (let i = 0; i < pointClouds.length; i++) {
            const pointCloud = pointClouds[i];
            if (pointCloud.disposed) {
                continue;
            }
            pointCloud.updateMaterial(camera, engine);
            pointCloud.updateVisibleBounds();
            pointCloud.updateBoundingBoxes();
        }
        this.lru.freeMemory();
        return result;
    }
    get pointBudget() {
        return this._pointBudget;
    }
    set pointBudget(value) {
        if (value !== this._pointBudget) {
            this._pointBudget = value;
            this.lru.pointBudget = value;
            this.lru.freeMemory();
        }
    }
    // @ts-ignore
    showFrustum(frustum) {
        // Display frustum planes in scene
        // This function creates visual representation of frustum planes for debugging
        const colors = [
            new Color3(1, 0, 0),
            new Color3(0, 1, 0),
            new Color3(0, 0, 1),
            new Color3(1, 1, 0),
            new Color3(1, 0, 1),
            new Color3(0, 1, 1), // Cyan
        ];
        // Remove existing frustum visualization if any
        if (this.frustumMeshes) {
            for (const mesh of this.frustumMeshes) {
                mesh.dispose();
            }
        }
        this.frustumMeshes = [];
        // Calculate frustum corners
        const nearPlane = frustum[0];
        const farPlane = frustum[1];
        const leftPlane = frustum[2];
        const rightPlane = frustum[3];
        const topPlane = frustum[4];
        const bottomPlane = frustum[5];
        // Calculate intersection points of the planes
        const corners = [];
        // Near plane corners
        const nearTopLeft = this.intersectPlanes(nearPlane, leftPlane, topPlane);
        const nearTopRight = this.intersectPlanes(nearPlane, rightPlane, topPlane);
        const nearBottomLeft = this.intersectPlanes(nearPlane, leftPlane, bottomPlane);
        const nearBottomRight = this.intersectPlanes(nearPlane, rightPlane, bottomPlane);
        // Far plane corners
        const farTopLeft = this.intersectPlanes(farPlane, leftPlane, topPlane);
        const farTopRight = this.intersectPlanes(farPlane, rightPlane, topPlane);
        const farBottomLeft = this.intersectPlanes(farPlane, leftPlane, bottomPlane);
        const farBottomRight = this.intersectPlanes(farPlane, rightPlane, bottomPlane);
        if (nearTopLeft &&
            nearTopRight &&
            nearBottomLeft &&
            nearBottomRight &&
            farTopLeft &&
            farTopRight &&
            farBottomLeft &&
            farBottomRight) {
            corners.push(nearTopLeft, nearTopRight, nearBottomLeft, nearBottomRight, farTopLeft, farTopRight, farBottomLeft, farBottomRight);
        }
        // Create faces of the frustum
        const faces = [
            // Near face
            [nearTopLeft, nearTopRight, nearBottomRight, nearBottomLeft],
            // Far face
            [farTopLeft, farTopRight, farBottomRight, farBottomLeft],
            // Left face
            [nearTopLeft, farTopLeft, farBottomLeft, nearBottomLeft],
            // Right face
            [nearTopRight, farTopRight, farBottomRight, nearBottomRight],
            // Top face
            [nearTopLeft, farTopLeft, farTopRight, nearTopRight],
            // Bottom face
            [nearBottomLeft, farBottomLeft, farBottomRight, nearBottomRight],
        ];
        // Create meshes for each face
        for (let i = 0; i < faces.length; i++) {
            const face = faces[i];
            const positions = [];
            const indices = [0, 1, 2, 0, 2, 3];
            // Add vertices
            for (const corner of face) {
                if (corner) {
                    positions.push(corner.x, corner.y, corner.z);
                }
            }
            // Create mesh
            const mesh = new Mesh('frustumFace' + i, null);
            const vertexData = new VertexData();
            vertexData.positions = positions;
            vertexData.indices = indices;
            vertexData.applyToMesh(mesh);
            // Apply material
            const material = new StandardMaterial('frustumMaterial' + i);
            material.diffuseColor = colors[i % colors.length];
            material.alpha = 0.3;
            material.backFaceCulling = false;
            mesh.material = material;
            this.frustumMeshes.push(mesh);
        }
    }
    intersectPlanes(p1, p2, p3) {
        // Calculate the intersection point of three planes
        const n1 = new Vector3(p1.normal.x, p1.normal.y, p1.normal.z);
        const n2 = new Vector3(p2.normal.x, p2.normal.y, p2.normal.z);
        const n3 = new Vector3(p3.normal.x, p3.normal.y, p3.normal.z);
        const det = Vector3.Dot(n1, Vector3.Cross(n2, n3));
        if (Math.abs(det) < 0.0001) {
            return null; // Planes are parallel or coincident
        }
        const d1 = p1.d;
        const d2 = p2.d;
        const d3 = p3.d;
        const cross23 = Vector3.Cross(n2, n3);
        const cross31 = Vector3.Cross(n3, n1);
        const cross12 = Vector3.Cross(n1, n2);
        const point = new Vector3(Vector3.Dot(cross23, n1) * d1 +
            Vector3.Dot(cross31, n2) * d2 +
            Vector3.Dot(cross12, n3) * d3).scale(1 / det);
        return point;
    }
    updateVisibility(pointClouds, camera, engine) {
        let numVisiblePoints = 0;
        const visibleNodes = [];
        // const unloadedGeometry: PointCloudOctreeGeometryNode[] = [];
        const unloadedGeometry = [];
        // calculate object space frustum and cam pos and setup priority queue
        const { frustums, cameraPositions, priorityQueue } = this.updateVisibilityStructures(pointClouds, camera);
        let loadedToGPUThisFrame = 0;
        let exceededMaxLoadsToGPU = false;
        let nodeLoadFailed = false;
        let queueItem;
        while ((queueItem = priorityQueue.pop()) !== undefined) {
            let node = queueItem.node;
            // If we will end up with too many points, we stop right away.
            if (numVisiblePoints + node.numPoints > this.pointBudget) {
                break;
            }
            const pointCloudIndex = queueItem.pointCloudIndex;
            const pointCloud = pointClouds[pointCloudIndex];
            const maxLevel = pointCloud.maxLevel !== undefined ? pointCloud.maxLevel : Infinity;
            const box = new BoundingBox(node.boundingBox.min, node.boundingBox.max);
            const isBoxInFrustum = box.isInFrustum(frustums[pointCloudIndex]);
            if (node.level > maxLevel || !isBoxInFrustum) {
                continue;
            }
            numVisiblePoints += node.numPoints;
            pointCloud.numVisiblePoints += node.numPoints;
            const parentNode = queueItem.parent;
            if (isGeometryNode(node) && (!parentNode || isTreeNode(parentNode))) {
                if (node.loaded && loadedToGPUThisFrame < MAX_LOADS_TO_GPU) {
                    // @ts-ignore
                    node = pointCloud.toTreeNode(node, parentNode);
                    loadedToGPUThisFrame++;
                }
                else if (node instanceof OctreeGeometryNode) {
                    if (node.loaded && loadedToGPUThisFrame >= MAX_LOADS_TO_GPU) {
                        exceededMaxLoadsToGPU = true;
                    }
                    unloadedGeometry.push(node);
                    // pointCloud.visibleGeometry.push(node as PointCloudOctreeGeometryNode);
                    pointCloud.visibleGeometry.push(node);
                }
                else {
                    nodeLoadFailed = true;
                    continue;
                }
            }
            if (isTreeNode(node)) {
                // @ts-ignore
                this.updateTreeNodeVisibility(pointCloud, node, visibleNodes);
                // @ts-ignore
                pointCloud.visibleGeometry.push(node.geometryNode);
            }
            const halfHeight = 0.5 * engine.getRenderHeight();
            this.updateChildVisibility(queueItem, priorityQueue, pointCloud, node, cameraPositions[pointCloudIndex], camera, halfHeight);
        } // end priority queue loop
        const numNodesToLoad = Math.min(this.maxNumNodesLoading, unloadedGeometry.length);
        const nodeLoadPromises = [];
        for (let i = 0; i < numNodesToLoad; i++) {
            nodeLoadPromises.push(unloadedGeometry[i].load());
        }
        return {
            visibleNodes,
            numVisiblePoints,
            exceededMaxLoadsToGPU,
            nodeLoadFailed,
            nodeLoadPromises,
        };
    }
    updateTreeNodeVisibility(pointCloud, node, visibleNodes) {
        this.lru.touch(node.geometryNode);
        const sceneNode = node.sceneNode;
        if (sceneNode.mesh !== undefined) {
            sceneNode.mesh.setEnabled(true);
            sceneNode.mesh.material = pointCloud.material;
        }
        visibleNodes.push(node);
        pointCloud.visibleNodes.push(node);
        this.updateBoundingBoxVisibility(pointCloud, node);
    }
    updateChildVisibility(queueItem, priorityQueue, pointCloud, node, cameraPosition, camera, halfHeight) {
        const children = node.children;
        for (let i = 0; i < children.length; i++) {
            const child = children[i];
            if (child === null) {
                continue;
            }
            const sphere = child.boundingSphere;
            const distance = sphere.center.subtract(cameraPosition).length();
            const radius = sphere.radius;
            let projectionFactor = 0.0;
            let weight = 0;
            let screenPixelRadius = 0;
            if (camera.mode === Camera.PERSPECTIVE_CAMERA) {
                const slope = Math.tan(camera.fov / 2.0);
                projectionFactor = halfHeight / (slope * distance);
                screenPixelRadius = radius * projectionFactor;
                weight =
                    distance < radius
                        ? Number.MAX_VALUE
                        : screenPixelRadius + 1 / distance;
            }
            else {
                console.error('Orthographic camera not supported');
            }
            // Don't add the node if it'll be too small on the screen.
            if (screenPixelRadius < pointCloud.minNodePixelSize) {
                continue;
            }
            // Nodes which are larger will have priority in loading/displaying.
            priorityQueue.push(new QueueItem(queueItem.pointCloudIndex, weight, child, node));
        }
    }
    updateBoundingBoxVisibility(pointCloud, node) {
        if (pointCloud.showBoundingBox && !node.boundingBoxNode) {
            const boxHelper = new Box3Helper(node.name, node.boundingBox, new Color3(1, 0, 0));
            pointCloud.boundingBoxNodes.push(boxHelper);
            node.boundingBoxNode = boxHelper;
            pointCloud.computeWorldMatrix(true);
            node.boundingBoxNode.setPreTransformMatrix(pointCloud.getWorldMatrix());
        }
        else if (pointCloud.showBoundingBox && node.boundingBoxNode) {
            node.boundingBoxNode.setEnabled(true);
            pointCloud.computeWorldMatrix(true);
            node.boundingBoxNode.setPreTransformMatrix(pointCloud.getWorldMatrix());
        }
        else if (!pointCloud.showBoundingBox && node.boundingBoxNode) {
            node.boundingBoxNode.setEnabled(false);
        }
    }
    updateVisibilityStructures = (() => {
        let frustumMatrix = new Matrix();
        let inverseWorldMatrix = new Matrix();
        let cameraMatrix = new Matrix();
        return (pointClouds, camera) => {
            const frustums = [];
            const cameraPositions = [];
            const priorityQueue = new BinaryHeap((x) => {
                return 1 / x.weight;
            });
            for (let i = 0; i < pointClouds.length; i++) {
                const pointCloud = pointClouds[i];
                if (!pointCloud.initialized()) {
                    continue;
                }
                pointCloud.numVisiblePoints = 0;
                pointCloud.visibleNodes = [];
                pointCloud.visibleGeometry = [];
                // Furstum in object space.
                const identityMatrix = Matrix.Identity();
                const inverseViewMatrix = camera.getWorldMatrix().clone().invert();
                const worldMatrix = pointCloud.getWorldMatrix();
                const cameraProjectionMatrix = camera.getProjectionMatrix(true);
                const aMatrix = cameraProjectionMatrix.multiply(identityMatrix);
                const bMatrix = inverseViewMatrix.multiply(aMatrix);
                const cMatrix = worldMatrix.multiply(bMatrix);
                frustumMatrix = cMatrix;
                const frustum = Frustum.GetPlanes(frustumMatrix);
                frustums.push(frustum);
                // Camera position in object space
                inverseWorldMatrix = worldMatrix.clone().invert();
                cameraMatrix = inverseWorldMatrix.multiply(Matrix.Identity());
                cameraMatrix = camera.getWorldMatrix().multiply(cameraMatrix);
                const cameraPosition = new Vector3(cameraMatrix.m[12], cameraMatrix.m[13], cameraMatrix.m[14]);
                cameraPositions.push(cameraPosition);
                if (pointCloud.isVisible && pointCloud.root !== null) {
                    const weight = Number.MAX_VALUE;
                    priorityQueue.push(new QueueItem(i, weight, pointCloud.root));
                }
                // Hide any previously visible nodes. We will later show only the needed ones.
                if (isTreeNode(pointCloud.root)) {
                    // @ts-ignore
                    pointCloud.hideDescendants(pointCloud?.root?.sceneNode?.mesh);
                }
                for (const boundingBoxNode of pointCloud.boundingBoxNodes) {
                    boundingBoxNode.setEnabled(false);
                }
            }
            return { frustums, cameraPositions, priorityQueue };
        };
    })();
}
